import * as React from "react";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Select, { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import DatePicker from "@amzn/awsui-components-react/polaris/date-picker";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { PrescriptionOption } from "./ConsumptionGuardOverridePage";
import { ApprovalReason } from "../../../consts/ApprovalsAndOverrides";

export interface OverrideDetailsData {
  prescriptionId: string;
  replacedOrderId: string;
  actualDepletionDate: string;
}

export const EMPTY_OVERRIDE_DETAILS: OverrideDetailsData = {
  prescriptionId: "",
  replacedOrderId: "",
  actualDepletionDate: "",
};

interface Props {
  reason: string;
  data: OverrideDetailsData;
  onChange: (updated: OverrideDetailsData) => void;
  prescriptions: PrescriptionOption[];
  selectedDrugAsin: string;
}

class OverrideDetailsFields extends React.Component<Props> {
  render() {
    const { reason, data, onChange, prescriptions, selectedDrugAsin } = this.props;

    // Filter prescriptions for the selected drug
    const drugPrescriptions = prescriptions.filter((p) => p.drugAsin === selectedDrugAsin);

    const prescriptionOptions: SelectProps.Option[] = drugPrescriptions.map((p) => ({
      label: `${p.prescriptionId} - ${p.daysSupply} days supply`,
      value: p.prescriptionId,
    }));

    const selectedPrescription = prescriptionOptions.find((o) => o.value === data.prescriptionId) || null;

    const showDoseChange = reason === ApprovalReason.DOSE_CHANGE;
    const showLostMedication = reason === ApprovalReason.LOST_MEDICATION;
    const showOnHandSupply = reason === ApprovalReason.ON_HAND_SUPPLY_ADJUSTMENT;

    if (!showDoseChange && !showLostMedication && !showOnHandSupply) {
      return null;
    }

    return (
      <ExpandableSection headerText="Override Details" defaultExpanded>
        <SpaceBetween size="m">
          {showDoseChange && (
            <FormField label="Prescription" description="New prescription for the dose change">
              <Select
                selectedOption={selectedPrescription}
                onChange={({ detail }) => onChange({ ...data, prescriptionId: detail.selectedOption.value || "" })}
                options={prescriptionOptions}
                placeholder="Select prescription..."
                filteringType="auto"
              />
            </FormField>
          )}

          {showLostMedication && (
            <FormField label="Replaced Order ID" description="Auto-populated from latest fill. Editable.">
              <Input
                value={data.replacedOrderId}
                onChange={({ detail }) => onChange({ ...data, replacedOrderId: detail.value })}
                placeholder="903-1234567-8901234"
              />
            </FormField>
          )}

          {showOnHandSupply && (
            <FormField label="Actual Depletion Date" description="Customer's actual medication depletion date">
              <DatePicker
                value={data.actualDepletionDate}
                onChange={({ detail }) => onChange({ ...data, actualDepletionDate: detail.value })}
                placeholder="YYYY/MM/DD"
              />
            </FormField>
          )}
        </SpaceBetween>
      </ExpandableSection>
    );
  }
}

export default OverrideDetailsFields;
